import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/use-mobile'
import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './sidebar'

const SIDEBAR_COLLAPSED_KEY = 'birdbrain-sidebar-collapsed'

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return saved === 'true'
  })
  const isMobile = useIsMobile()
  const location = useLocation()

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed))
  }, [isCollapsed])

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }, [location.pathname, isMobile])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen, isMobile])

  const toggleCollapsed = () => setIsCollapsed(prev => !prev)

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isCollapsed && !isMobile ? 'w-16' : 'w-64'} `}
      >
        <Sidebar
          isCollapsed={isCollapsed && !isMobile}
          onToggleCollapse={toggleCollapsed}
          onCloseMobile={() => setSidebarOpen(false)}
          isMobileSidebarOpen={sidebarOpen}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header bar */}
        <header className="flex h-14 items-center border-b border-border/50 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="touch-target"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex flex-1 items-center justify-center">
            <div className="flex items-center gap-2">
              <img src="/icon.png" alt="BirdBrain" className="h-7 w-7" />
              <span className="text-sm font-semibold">BirdBrain</span>
            </div>
          </div>
          {/* Spacer to balance the layout */}
          <div className="w-10" />
        </header>

        {/* Main Content Area */}
        <main className="pb-safe flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
